// src/components/Sidebar.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, ShoppingCart, ClipboardList, FileText, Receipt, Users, Package,
  Wrench, Landmark, Building, Moon, Sun, LogOut, Calendar, Truck,
  ChevronDown, ChevronsLeft, ChevronsRight
} from 'lucide-react';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { signOut } from 'firebase/auth';
import { auth } from '../firebaseConfig';

/* ---------------- Item do menu (com tooltip quando colapsado) ---------------- */
const NavItem = ({ to, icon, label, location, isOpen, onLinkClick }) => {
  const isActive = location.pathname === to;
  const Icon = icon;
  return (
    <li>
      <Link
        to={to}
        title={!isOpen ? label : ''}
        onClick={onLinkClick}
        className={`flex items-center p-3 rounded-lg transition-colors text-gray-200 
        ${isActive ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'}`}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        {isOpen && <span className="ml-4 whitespace-nowrap">{label}</span>}
      </Link>
    </li>
  );
};

/* ---------------- Secção expansível (estado persistente) ---------------- */
const NavSection = ({ title, children, defaultOpen = false, storageKey }) => {
  const [isSectionOpen, setIsSectionOpen] = useState(() => {
    if (!storageKey) return defaultOpen;
    const saved = localStorage.getItem(storageKey);
    return saved === null ? defaultOpen : saved === 'true';
  });

  useEffect(() => {
    if (storageKey) localStorage.setItem(storageKey, isSectionOpen ? 'true' : 'false');
  }, [isSectionOpen, storageKey]);

  return (
    <div className="mb-6">
      <button
        onClick={() => setIsSectionOpen(!isSectionOpen)}
        className="w-full flex items-center justify-between px-3 text-xs font-semibold uppercase text-white/50 mb-2 transition-colors hover:text-white/80"
        title={isSectionOpen ? 'Recolher' : 'Expandir'}
      >
        {title}
        <ChevronDown size={16} className={`transition-transform ${isSectionOpen ? 'rotate-180' : ''}`} />
      </button>
      {isSectionOpen && <ul className="space-y-2">{children}</ul>}
    </div>
  );
};

export default function Sidebar({
  // compat: aceita ambos os nomes usados no App
  isMobileOpen,
  setIsMobileOpen,
  isSidebarOpen,
  setIsSidebarOpen
}) {
  const location = useLocation();
  const navigate = useNavigate();

  // normaliza props para funcionar com os dois padrões
  const mobileOpen = typeof isMobileOpen === 'boolean' ? isMobileOpen : !!isSidebarOpen;
  const setMobileOpen = typeof setIsMobileOpen === 'function' ? setIsMobileOpen : setIsSidebarOpen;

  const [isDesktopExpanded, setIsDesktopExpanded] = useState(() => {
    const saved = localStorage.getItem('sidebarExpanded');
    return saved === null ? true : saved === 'true';
  });
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('darkMode', darkMode ? 'true' : 'false');
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('sidebarExpanded', isDesktopExpanded ? 'true' : 'false');
  }, [isDesktopExpanded]);

  const handleLogout = async () => {
    const result = await Swal.fire({
      title: 'Sair do sistema?',
      text: 'Tem certeza que deseja sair?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, sair',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    });
    if (result.isConfirmed) {
      await signOut(auth);
      navigate('/login');
    }
  };

  const handleLinkClick = () => {
    if (window.innerWidth < 768 && typeof setMobileOpen === 'function') setMobileOpen(false);
  };

  /* ---------------- Menus (ordenados alfabeticamente por label) ---------------- */
  const byLabel = (a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' });

  const operacionalUnsorted = useMemo(() => ([
    { to: '/', icon: LayoutDashboard, label: 'Início' },
    { to: '/agenda', icon: Calendar, label: 'Agenda' },
    { to: '/notas', icon: FileText, label: 'Notas Fiscais' },          // ⬅️ NOVO
    { to: '/orcamentos', icon: Receipt, label: 'Orçamentos' },
    { to: '/os', icon: ClipboardList, label: 'Ordens de Serviço' },
    { to: '/vendas-balcao', icon: ShoppingCart, label: 'Vender Balcão' },
  ]), []);

  const cadastrosUnsorted = useMemo(() => ([
    { to: '/caixa', icon: Landmark, label: 'Caixa' },
    { to: '/clientes', icon: Users, label: 'Clientes' },
    { to: '/fornecedores', icon: Truck, label: 'Fornecedores' },
    { to: '/produtos', icon: Package, label: 'Produtos' },
    { to: '/servicos', icon: Wrench, label: 'Serviços' },
  ]), []);

  const menuOperacional = useMemo(() => [...operacionalUnsorted].sort(byLabel), [operacionalUnsorted]);
  const menuCadastros   = useMemo(() => [...cadastrosUnsorted].sort(byLabel),   [cadastrosUnsorted]);

  const isOpen = isDesktopExpanded;

  return (
    <>
      {/* Overlay mobile */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden transition-opacity ${mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={() => typeof setMobileOpen === 'function' && setMobileOpen(false)}
      />

      <aside
        className={`bg-gestor-blue text-white flex flex-col h-screen fixed top-0 left-0 z-40 transition-all duration-300 
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} 
        md:relative md:translate-x-0 
        ${isDesktopExpanded ? 'w-64' : 'w-20'}
        overflow-hidden`}   /* evita barra externa */
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 h-16">
          {isDesktopExpanded && <span className="text-xl font-bold">GestorPro</span>}
          <button
            onClick={() => setIsDesktopExpanded(!isDesktopExpanded)}
            className="p-2 rounded-lg hover:bg-white/10 hidden md:block"
            title={isDesktopExpanded ? 'Recolher menu' : 'Expandir menu'}
            aria-label={isDesktopExpanded ? 'Recolher menu' : 'Expandir menu'}
          >
            {isDesktopExpanded ? <ChevronsLeft size={20} /> : <ChevronsRight size={20} />}
          </button>
        </div>

        {/* Navegação */}
        <nav className="flex-grow min-h-0 px-3 py-4 overflow-y-auto no-scrollbar">
          {isDesktopExpanded ? (
            <>
              {/* Operacional no topo */}
              <NavSection title="Operacional" defaultOpen={true} storageKey="sec_operacional_open">
                {menuOperacional.map(item => (
                  <NavItem key={item.to} {...item} location={location} isOpen={isOpen} onLinkClick={handleLinkClick} />
                ))}
              </NavSection>

              <NavSection title="Cadastros" defaultOpen={false} storageKey="sec_cadastros_open">
                {menuCadastros.map(item => (
                  <NavItem key={item.to} {...item} location={location} isOpen={isOpen} onLinkClick={handleLinkClick} />
                ))}
              </NavSection>
            </>
          ) : (
            <>
              {/* Vista encolhida: só ícones */}
              <ul className="space-y-2">
                {menuOperacional.map(item => (
                  <NavItem key={item.to} {...item} location={location} isOpen={isOpen} onLinkClick={handleLinkClick} />
                ))}
              </ul>
              <hr className="my-4 border-white/10" />
              <ul className="space-y-2">
                {menuCadastros.map(item => (
                  <NavItem key={item.to} {...item} location={location} isOpen={isOpen} onLinkClick={handleLinkClick} />
                ))}
              </ul>
            </>
          )}
        </nav>

        {/* Rodapé */}
        <div className="px-3 py-4 border-t border-white/10">
          <ul className="space-y-2">
            <NavItem
              to="/empresas"
              icon={Building}
              label="Minha Empresa"
              location={location}
              isOpen={isOpen}
              onLinkClick={handleLinkClick}
            />
            <li>
              <button
                onClick={() => setDarkMode(!darkMode)}
                title={!isOpen ? (darkMode ? 'Modo Claro' : 'Modo Escuro') : ''}
                className="flex items-center p-3 rounded-lg w-full text-left text-gray-200 hover:bg-white/10"
              >
                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
                {isOpen && <span className="ml-4">{darkMode ? 'Modo Claro' : 'Modo Escuro'}</span>}
              </button>
            </li>
            <li>
              <button
                onClick={handleLogout}
                title={!isOpen ? 'Sair' : ''}
                className="flex items-center p-3 rounded-lg w-full text-left text-gray-200 hover:bg-white/10"
              >
                <LogOut size={20} />
                {isOpen && <span className="ml-4">Sair</span>}
              </button>
            </li>
          </ul>
        </div>
      </aside>
    </>
  );
}
